import React, { FC, Fragment, useCallback, useEffect, useRef, useState } from "react";
import { styled } from "@mui/system";
import extensibilitySdk from "@outreach/extensibility-sdk";
import { linkedInCard, LinkedInCardResult } from "../../services/extension";

/**
 * The LinkedIn profile and LinkedIn activity cards as native Outreach prospect
 * TILES, so a rep adds them from "Add tiles" and Outreach's own layout editor
 * moves, resizes and saves them like any other card.
 *
 * They show what the cards the extension injects show (enrich/linkedin-cards.js)
 * and read the same data the same way: the extension answers
 * "boring:linkedinCard" with the rep's own LinkedIn session, from the prospect
 * id in the tile's context (it looks up the LinkedIn URL itself). Outreach
 * draws the tile's frame and title; this is the body, in the injected cards'
 * measured values.
 */

const INK = "#454262";
const MUTED = "#686783";
const BRAND = "#5E5EAF";
const LINK = "#5951FF";
const RULE = "#E8EAF1";
const SOFT = "#F2F2F7";
const CANVAS = "#fefdfc"; // measured: the panel Outreach paints behind the frame
const OUTREACH_SANS = '"Roboto Flex", Roboto, "Helvetica Neue", Arial, sans-serif';

const isLocalHost = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

// Errors that leave the outcome of a react or comment unknown: the request
// may still be running, or have landed. The next try reuses the same request
// id, so the extension answers it rather than posting twice.
const UNSETTLED = new Set(["extension_timeout", "extension_unavailable", "in_flight", "outcome_unknown"]);

const newRequestId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

const errorText = (error?: string, crm: "Outreach" | "HubSpot" = "Outreach") => {
  switch (error) {
    case "not_logged_in_linkedin":
      return "Log in to LinkedIn in this browser to see this.";
    case "profile_not_found":
      return "LinkedIn did not find this profile.";
    case "no_linkedin_url":
      return crm === "HubSpot" ? "This contact has no LinkedIn profile in HubSpot." : "This prospect has no LinkedIn profile in Outreach.";
    case "not_allowed":
      return "LinkedIn cards are not on for your boring seat.";
    case "extension_unavailable":
      return "Install the boring extension in this browser, and sign in to it, to see this.";
    case "outcome_unknown":
    case "in_flight":
      return "LinkedIn has not confirmed that yet. Check on LinkedIn before trying again.";
    default:
      return "LinkedIn did not answer just now. Try again in a minute.";
  }
};

/* ── Types, as the extension answers them (enrich/linkedin-card.js) ── */

interface Role {
  title?: string | null;
  company?: string | null;
  companyUrl?: string | null;
  logoUrl?: string | null;
  dates?: string | null;
  duration?: string | null;
  current?: boolean;
}

interface School {
  school: string;
  degree?: string | null;
  dates?: string | null;
  logoUrl?: string | null;
}

interface Profile {
  url?: string;
  name?: string;
  headline?: string | null;
  location?: string | null;
  picture?: string | null;
  experience?: Role[];
  education?: School[];
}

interface Actor {
  name?: string | null;
  headline?: string | null;
  picture?: string | null;
}

interface ActivityItem {
  url?: string | null;
  activityUrn?: string | null;
  threadUrn?: string | null;
  socialDetailUrn?: string | null;
  liked?: boolean;
  myReaction?: string | null;
  header?: string | null;
  author?: Actor | null;
  text?: string | null;
  reshared?: { author?: Actor | null; text?: string | null; age?: string | null } | null;
  comment?: { name?: string | null; picture?: string | null; text?: string | null } | null;
  age?: string | null;
  reactions?: number;
  comments?: number;
  reposts?: number;
}

interface Person {
  name?: string | null;
  headline?: string | null;
  picture?: string | null;
  url?: string | null;
  reaction?: string | null;
  text?: string | null;
  createdAt?: number | null;
  commentUrn?: string | null;
  reactions?: number;
  replies?: number;
  myReaction?: string | null;
}

/* ── Page ── */

const Page = styled("div")`
  font-family: ${OUTREACH_SANS};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 14px;
  line-height: 20px;
  color: ${INK};
  background: ${CANVAS};
  height: 100vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const Bar = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  padding: 0 16px 10px;
`;

/** The activity tabs when they sit under the profile, inside the scroll. */
const InlineBar = styled(Bar)`
  padding: 20px 0 10px;
`;

const Body = styled("div")`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 16px 16px;
`;

const OpenLink = styled("a")`
  margin-left: auto;
  color: ${LINK};
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const Note = styled("p")`
  margin: 8px 0 0;
  color: ${MUTED};
`;

const Meta = styled("div")`
  font-size: 13px;
  line-height: 16px;
  color: ${MUTED};
`;

const MetaSpan = styled("span")`
  font-size: 13px;
  line-height: 16px;
  color: ${MUTED};
`;

const Bone = styled("div")`
  height: 12px;
  margin: 10px 0;
  border-radius: 6px;
  background: linear-gradient(90deg, #efeff4 25%, #f7f7fa 50%, #efeff4 75%);
  background-size: 200% 100%;
  animation: boring-li-shimmer 1.2s ease-in-out infinite;
  @keyframes boring-li-shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;

const Bones: FC<{ lines?: number }> = ({ lines = 5 }) => (
  <>
    {Array.from({ length: lines }, (_, i) => (
      <Bone key={i} style={{ width: `${[88, 64, 76, 52, 70][i % 5]}%` }} />
    ))}
  </>
);

/* ── A round picture over its initials, which stay if the picture fails ── */

const initialsFor = (name?: string | null) => {
  const words = String(name || "")
    .replace(/\([^)]*\)|\[[^\]]*\]/g, " ")
    .split(",")[0]
    .split(/\s+/)
    .filter((word) => /^\p{L}/u.test(word) && !word.includes("/"));
  if (!words.length) return "?";
  const first = (word: string) => Array.from(word)[0].toUpperCase();
  return words.length === 1 ? first(words[0]) : first(words[0]) + first(words[words.length - 1]);
};

const CircleBox = styled("div")<{ logo?: boolean; current?: boolean }>`
  position: relative;
  flex: 0 0 auto;
  border-radius: 50%;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  line-height: 1;
  background: ${({ logo }) => (logo ? SOFT : RULE)};
  color: ${({ logo }) => (logo ? MUTED : BRAND)};
  border: ${({ logo, current }) => (logo ? `1px solid ${current ? BRAND : RULE}` : "none")};
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: #fff;
  }
`;

const Circle: FC<{ url?: string | null; name?: string | null; size?: number; logo?: boolean; current?: boolean }> = ({
  url,
  name,
  size = 40,
  logo,
  current,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <CircleBox logo={logo} current={current} style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}>
      {initialsFor(name)}
      {url && !failed && (
        <img
          alt=""
          referrerPolicy="no-referrer"
          src={url}
          style={{ opacity: loaded ? 1 : 0 }}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
    </CircleBox>
  );
};

/* ── Profile ── */

const PersonHead = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${RULE};
`;

const Who = styled("div")`
  min-width: 0;
  flex: 1 1 auto;
`;

const Name = styled("span")`
  font-weight: 600;
  color: ${INK};
`;

const Headline = styled("div")<{ oneLine?: boolean }>`
  font-size: 13px;
  line-height: 17px;
  color: ${MUTED};
  display: -webkit-box;
  -webkit-line-clamp: ${({ oneLine }) => (oneLine ? 1 : 2)};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Block = styled("section")`
  padding-top: 16px;
`;

const SectionTitle = styled("div")`
  margin: 0 0 8px;
  font-weight: 600;
`;

const Timeline = styled("ul")`
  list-style: none;
  margin: 0;
  padding: 0;
  > li {
    position: relative;
    display: flex;
    gap: 12px;
    padding-bottom: 14px;
  }
  > li:last-child {
    padding-bottom: 0;
  }
  > li:not(:last-child) > .rail::after {
    content: "";
    position: absolute;
    top: 40px;
    bottom: -10px;
    left: 50%;
    width: 1px;
    background: ${RULE};
  }
  .rail {
    position: relative;
    flex: 0 0 36px;
    display: flex;
    justify-content: center;
  }
  .tl-body {
    min-width: 0;
    padding-top: 1px;
  }
  .tl-title {
    font-weight: 600;
  }
`;

// Several roles at one company: each on a small dot, joined by a hairline.
const Roles = styled("ul")`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  > li {
    position: relative;
    padding: 0 0 10px 16px;
  }
  > li:last-child {
    padding-bottom: 0;
  }
  > li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 7px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #c9c8d9;
  }
  > li.current::before {
    background: ${BRAND};
  }
  > li:not(:last-child)::after {
    content: "";
    position: absolute;
    left: 3px;
    top: 18px;
    bottom: 2px;
    width: 1px;
    background: ${RULE};
  }
`;

/** Back-to-back roles at one company, as LinkedIn groups them. A return to a company later is its own group. */
const companyGroups = (roles: Role[]) => {
  const keyOf = (role: Role) => (role.companyUrl || role.company || "").toLowerCase() || null;
  const groups: { key: string | null; roles: Role[] }[] = [];
  for (const role of roles) {
    const last = groups[groups.length - 1];
    const key = keyOf(role);
    if (last && key && last.key === key) last.roles.push(role);
    else groups.push({ key, roles: [role] });
  }
  return groups;
};

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/** "Mar 2026" or "2019" as a month count; "Present" is this month. */
const monthIndex = (value: string | undefined, end: boolean) => {
  const textValue = String(value || "").trim();
  if (/present|current/i.test(textValue)) {
    const now = new Date();
    return now.getFullYear() * 12 + now.getMonth();
  }
  const m = textValue.match(/^([A-Za-z]{3})[a-z]*\s+(\d{4})$/);
  if (m && MONTHS.includes(m[1].toLowerCase())) return Number(m[2]) * 12 + MONTHS.indexOf(m[1].toLowerCase());
  const y = textValue.match(/^(\d{4})$/);
  return y ? Number(y[1]) * 12 + (end ? 11 : 0) : null;
};

/** "2 yrs 3 mos" between two LinkedIn dates, counting both ends' months as LinkedIn does. */
const tenure = (start?: string, end?: string) => {
  const a = monthIndex(start, false);
  const b = monthIndex(end, true);
  if (a == null || b == null || b < a) return null;
  const total = b - a + 1;
  const years = Math.floor(total / 12);
  const months = total % 12;
  const parts = [];
  if (years) parts.push(`${years} yr${years > 1 ? "s" : ""}`);
  if (months) parts.push(`${months} mo${months > 1 ? "s" : ""}`);
  return parts.join(" ") || null;
};

const datesLine = (dates?: string | null, duration?: string | null) => [dates, duration].filter(Boolean).join(" · ") || null;

/** "Jan 2026 - Aug 2026 · 8 mos": the latest role's end back to the earliest role's start. */
const groupSpan = (roles: Role[]) => {
  const [, end] = String(roles[0].dates || "").split(/\s+-\s+/);
  const [start] = String(roles[roles.length - 1].dates || "").split(/\s+-\s+/);
  return start && end ? datesLine(`${start} - ${end}`, tenure(start, end)) : null;
};

const TimelineRow: FC<{ logoUrl?: string | null; logoName?: string | null; current?: boolean; children: React.ReactNode }> = ({
  logoUrl,
  logoName,
  current,
  children,
}) => (
  <li>
    <div className="rail">
      <Circle url={logoUrl} name={logoName} size={36} logo current={current} />
    </div>
    <div className="tl-body">{children}</div>
  </li>
);

const ExperienceRow: FC<{ roles: Role[] }> = ({ roles }) => {
  const first = roles[0];
  const current = roles.some((role) => role.current);
  if (roles.length === 1) {
    return (
      <TimelineRow logoUrl={first.logoUrl} logoName={first.company || first.title} current={current}>
        {first.title && <div className="tl-title">{first.title}</div>}
        {first.company && <div>{first.company}</div>}
        {datesLine(first.dates, first.duration) && <Meta>{datesLine(first.dates, first.duration)}</Meta>}
      </TimelineRow>
    );
  }
  return (
    <TimelineRow logoUrl={roles.find((role) => role.logoUrl)?.logoUrl} logoName={first.company} current={current}>
      {first.company && <div className="tl-title">{first.company}</div>}
      {groupSpan(roles) && <Meta>{groupSpan(roles)}</Meta>}
      <Roles>
        {roles.map((role, i) => (
          <li key={i} className={role.current ? "current" : undefined}>
            <div className="tl-title">{role.title}</div>
            {datesLine(role.dates, role.duration) && <Meta>{datesLine(role.dates, role.duration)}</Meta>}
          </li>
        ))}
      </Roles>
    </TimelineRow>
  );
};

const ProfileBody: FC<{ profile: Profile }> = ({ profile }) => (
  <>
    <PersonHead>
      <Circle url={profile.picture} name={profile.name} size={48} />
      <Who>
        <Name>{profile.name}</Name>
        {profile.headline && <Headline>{profile.headline}</Headline>}
        {profile.location && <Meta>{profile.location}</Meta>}
      </Who>
    </PersonHead>
    {!!profile.experience?.length && (
      <Block>
        <SectionTitle>Experience</SectionTitle>
        <Timeline>
          {companyGroups(profile.experience).map((group, i) => (
            <ExperienceRow key={i} roles={group.roles} />
          ))}
        </Timeline>
      </Block>
    )}
    {!!profile.education?.length && (
      <Block>
        <SectionTitle>Education</SectionTitle>
        <Timeline>
          {profile.education.map((school, i) => (
            <TimelineRow key={i} logoUrl={school.logoUrl} logoName={school.school}>
              <div className="tl-title">{school.school}</div>
              {school.degree && <div>{school.degree}</div>}
              {school.dates && <Meta>{school.dates}</Meta>}
            </TimelineRow>
          ))}
        </Timeline>
      </Block>
    )}
  </>
);

/* ── Activity ── */

const KINDS = [
  { kind: "posts", label: "Posts", empty: "No recent posts on LinkedIn." },
  { kind: "comments", label: "Comments", empty: "No recent comments on LinkedIn." },
  { kind: "reactions", label: "Reactions", empty: "No recent reactions on LinkedIn." },
];

const Tab = styled("button")`
  height: 28px;
  padding: 0 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  color: ${BRAND};
  font: 600 14px/21px ${OUTREACH_SANS};
  &:hover,
  &[aria-selected="true"] {
    background: #f2f2ff;
  }
  &[aria-selected="true"] {
    color: ${LINK};
  }
`;

const Posts = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Post = styled("article")`
  border: 1px solid ${RULE};
  border-radius: 8px;
  padding: 12px;
`;

const Context = styled("div")`
  margin: -12px -12px 12px;
  padding: 8px 12px;
  border-bottom: 1px solid ${RULE};
  font-size: 13px;
  line-height: 16px;
  color: ${MUTED};
`;

const BylineRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const BylineTop = styled("div")`
  display: flex;
  align-items: baseline;
  gap: 2px;
  min-width: 0;
  > :first-of-type {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  > span:last-of-type {
    white-space: nowrap;
  }
`;

/* Full text, the author's own line breaks kept. */
const Text = styled("div")<{ small?: boolean }>`
  font-size: ${({ small }) => (small ? 13 : 14)}px;
  line-height: ${({ small }) => (small ? 19 : 20)}px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Embed = styled("div")`
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid ${RULE};
  border-radius: 8px;
  background: #fbfaf8;
`;

const Counts = styled("div")`
  margin-top: 10px;
  font-size: 13px;
  line-height: 16px;
  color: ${MUTED};
`;

const CountButton = styled("button")`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  &:hover,
  &[aria-expanded="true"] {
    color: ${LINK};
    text-decoration: underline;
  }
`;

const Actions = styled("div")`
  display: flex;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${RULE};
`;

const ActionButton = styled("button")`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 100px;
  background: transparent;
  color: ${MUTED};
  cursor: pointer;
  font: 600 13px/1 ${OUTREACH_SANS};
  svg {
    width: 16px;
    height: 16px;
  }
  &:hover {
    background: ${SOFT};
    color: ${INK};
  }
  &.is-link {
    margin-left: auto;
  }
`;

const LinkAction = styled("button")`
  padding: 2px 4px;
  margin: 0 -4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  font: 600 12px/16px ${OUTREACH_SANS};
  color: ${MUTED};
  &:hover {
    background: ${SOFT};
    color: ${INK};
  }
`;

const Input = styled("textarea")`
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 60px;
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 8px;
  background: #fff;
  color: ${INK};
  font: 14px/20px ${OUTREACH_SANS};
  outline: none;
  &:focus {
    border-color: #7971ff;
  }
`;

const ComposerRow = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

const Pill = styled("button")<{ primary?: boolean }>`
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 100px;
  background: ${({ primary }) => (primary ? BRAND : RULE)};
  color: ${({ primary }) => (primary ? "#fff" : BRAND)};
  cursor: pointer;
  font: 600 14px/1 ${OUTREACH_SANS};
  &:hover:not(:disabled) {
    background: ${({ primary }) => (primary ? "#3F3B98" : "#DCDEEA")};
  }
  &:disabled {
    background: #f6f6f7;
    color: #a3a2bd;
    cursor: default;
  }
`;

const Flash = styled("div")`
  margin-top: 8px;
  font-size: 13px;
  line-height: 16px;
  color: ${MUTED};
`;

const People = styled("div")`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${RULE};
`;

/* No scroll of its own: the tile body scrolls, and an inner scroll would clip the reaction pill. */
const PeopleList = styled("ul")`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  > li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  a {
    color: ${INK};
    text-decoration: none;
    font-weight: 600;
  }
  a:hover {
    color: ${LINK};
    text-decoration: underline;
  }
`;

const CommentActions = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`;

/* LinkedIn's reaction button: a click likes; a hover opens the six reactions. */
const ReactWrap = styled("div")`
  position: relative;
  display: inline-flex;
`;

const Picker = styled("div")<{ open: boolean; compact?: boolean }>`
  position: absolute;
  bottom: calc(100% + ${({ compact }) => (compact ? 4 : 6)}px);
  left: -4px;
  z-index: 5;
  display: flex;
  gap: 2px;
  padding: 4px 6px;
  border-radius: 100px;
  background: ${CANVAS};
  box-shadow: rgba(69, 66, 98, 0.16) 0 0 1px 0, rgba(69, 66, 98, 0.24) 0 4px 12px 0;
  opacity: ${({ open }) => (open ? 1 : 0)};
  transform: ${({ open }) => (open ? "none" : "translateY(4px)")};
  pointer-events: ${({ open }) => (open ? "auto" : "none")};
  transition: opacity 0.12s ease, transform 0.12s ease;
`;

const PickerOption = styled("button")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: transform 0.12s ease, background-color 0.12s ease;
  svg {
    width: 20px;
    height: 20px;
  }
  &:hover {
    background: ${SOFT};
    transform: translateY(-2px);
  }
`;

const REACTIONS: Record<string, string> = { LIKE: "Like", PRAISE: "Celebrate", EMPATHY: "Love", INTEREST: "Insightful", APPRECIATION: "Support", ENTERTAINMENT: "Funny" };
// LinkedIn's reactions, by API name, in its own picker order.
const REACTION_ORDER = ["LIKE", "PRAISE", "APPRECIATION", "EMPATHY", "INTEREST", "ENTERTAINMENT"];
// Each reaction in its own muted colour, in the pill and on the rep's button.
const REACTION_COLORS: Record<string, string> = {
  LIKE: LINK,
  PRAISE: "#44712E",
  APPRECIATION: "#715E86",
  EMPATHY: "#B24020",
  INTEREST: "#915907",
  ENTERTAINMENT: "#1A707E",
};

const LineIcon: FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

// The injected cards' line icons for LinkedIn's six reactions (no emoji).
const REACTION_ICONS: Record<string, React.ReactNode> = {
  LIKE: (
    <LineIcon>
      <path d="M7 10.5V20H4.5a1 1 0 0 1-1-1v-7.5a1 1 0 0 1 1-1H7Zm0 0 3.8-6.6a1.9 1.9 0 0 1 3.4 1.5L13.4 9H18a2 2 0 0 1 2 2.4l-1.3 6.8A2.2 2.2 0 0 1 16.5 20H7" />
    </LineIcon>
  ),
  PRAISE: (
    <LineIcon>
      <path d="M11 3.5l1.7 4.3 4.3 1.7-4.3 1.7L11 15.5l-1.7-4.3L5 9.5l4.3-1.7Z" />
      <path d="M18 14.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8Z" />
    </LineIcon>
  ),
  APPRECIATION: (
    <LineIcon>
      <path d="M12 11s-3.2-1.9-3.2-4a1.8 1.8 0 0 1 3.2-1.1A1.8 1.8 0 0 1 15.2 7c0 2.1-3.2 4-3.2 4Z" />
      <path d="M3 14.5h2.5l3 2H12a1.5 1.5 0 0 1 0 3H8.5" />
      <path d="M12.5 19.5H16l4.2-4.2a1.5 1.5 0 0 0-2.1-2.1L15.5 15.8" />
    </LineIcon>
  ),
  EMPATHY: (
    <LineIcon>
      <path d="M12 20s-7.5-4.6-7.5-10.2A4.2 4.2 0 0 1 12 7.1a4.2 4.2 0 0 1 7.5 2.7C19.5 15.4 12 20 12 20Z" />
    </LineIcon>
  ),
  INTEREST: (
    <LineIcon>
      <path d="M9.5 18h5M10.5 21h3" />
      <path d="M12 3a6 6 0 0 0-3.6 10.8c.7.5 1.1 1.3 1.1 2.1v.1h5v-.1c0-.8.4-1.6 1.1-2.1A6 6 0 0 0 12 3Z" />
    </LineIcon>
  ),
  ENTERTAINMENT: (
    <LineIcon>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8 13.5h8a4 4 0 0 1-8 0Z" />
      <path d="M8.5 9.5l1.6 1M15.5 9.5l-1.6 1" />
    </LineIcon>
  ),
};

const ThumbIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M5 7v7H2.5A.5.5 0 0 1 2 13.5v-6a.5.5 0 0 1 .5-.5H5Zm1.5 7V7l2.3-4.6a1 1 0 0 1 1.9.6L10.2 6h3.3a1.5 1.5 0 0 1 1.5 1.8l-1.1 5A1.5 1.5 0 0 1 12.4 14H6.5Z"
      fill="currentColor"
    />
  </svg>
);
const CommentIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M2 3.5A1.5 1.5 0 0 1 3.5 2h9A1.5 1.5 0 0 1 14 3.5v6a1.5 1.5 0 0 1-1.5 1.5H7l-3.2 2.6A.5.5 0 0 1 3 13.2V11h0.5A1.5 1.5 0 0 1 2 9.5v-6Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
  </svg>
);
const OpenIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M9 2h5v5M14 2 7.5 8.5M12 9.5V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** "3d", "2w", "4mo", LinkedIn's own way of dating a comment. */
const ago = (ts?: number | null) => {
  if (!ts) return null;
  const minutes = Math.max(0, (Date.now() - ts) / 60000);
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = hours / 24;
  if (days < 7) return `${Math.round(days)}d`;
  if (days < 30) return `${Math.round(days / 7)}w`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
};

const count = (n: number | undefined, one: string, many: string) => (n ? `${n} ${n === 1 ? one : many}` : null);

// eslint-disable-next-line @typescript-eslint/ban-types
type Send = <T = {}>(message: Record<string, unknown> & { part: string }) => Promise<LinkedInCardResult<T>>;
type PeopleAnswer = { people?: Person[]; total?: number };

/**
 * LinkedIn's reaction button: a click likes (or takes back the rep's
 * reaction); hovering opens a small pill of the six reactions. The rep's
 * reaction and the count change at once and are put back if LinkedIn says no.
 * Every click is its own request id, sent once (see background.js onceOnly).
 */
const ReactionControl: FC<{
  mine: string | null;
  compact?: boolean;
  apply: (on: boolean, type: string, previous: string | null, requestId: string) => Promise<{ ok: boolean; error?: string }>;
  onChange: (mine: string | null, delta: number) => void;
  onError: (error?: string) => void;
}> = ({ mine, compact, apply, onChange, onError }) => {
  const [open, setOpen] = useState(false);
  const busy = useRef(false);
  const timer = useRef<number>();

  const run = async (type: string | null) => {
    if (busy.current) return;
    const previous = mine;
    // The same reaction again takes it back; another one replaces it.
    const on = !(previous && (!type || type === previous));
    const next = on ? type || "LIKE" : null;
    const delta = (previous ? 0 : 1) - (next ? 0 : 1);
    busy.current = true;
    setOpen(false);
    onChange(next, delta);
    const response = await apply(on, next || previous || "LIKE", previous, newRequestId());
    busy.current = false;
    if (!response.ok) {
      onChange(previous, -delta);
      onError(response.error);
    }
  };

  const color = mine ? REACTION_COLORS[mine] || LINK : undefined;
  const label = mine ? REACTIONS[mine] || "Liked" : "Like";
  return (
    <ReactWrap
      onMouseEnter={() => {
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => !busy.current && setOpen(true), 350);
      }}
      onMouseLeave={() => {
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setOpen(false), 200);
      }}
    >
      {compact ? (
        <LinkAction type="button" style={{ color }} onClick={() => run(null)}>
          {mine ? REACTIONS[mine] || "Like" : "Like"}
        </LinkAction>
      ) : (
        <ActionButton type="button" style={{ color }} onClick={() => run(null)}>
          {mine && mine !== "LIKE" ? REACTION_ICONS[mine] : <ThumbIcon />}
          <span>{label}</span>
        </ActionButton>
      )}
      <Picker open={open} compact={compact} role="menu">
        {REACTION_ORDER.map((type) => (
          <PickerOption
            key={type}
            type="button"
            title={REACTIONS[type]}
            aria-label={REACTIONS[type]}
            style={{ color: REACTION_COLORS[type] }}
            onClick={() => run(type)}
          >
            {REACTION_ICONS[type]}
          </PickerOption>
        ))}
      </Picker>
    </ReactWrap>
  );
};

/**
 * A comment box that posts to LinkedIn as the rep, shown as posted only once
 * LinkedIn has taken it. The request id stays the same until LinkedIn gives a
 * definite answer, so a retry after a timeout can never post a second copy.
 */
const Composer: FC<{
  placeholder: string;
  action: string;
  busyLabel: string;
  post: (text: string, requestId: string) => Promise<{ ok: boolean; error?: string }>;
  onPosted: () => void;
  onClose: () => void;
  onError: (error?: string) => void;
}> = ({ placeholder, action, busyLabel, post, onPosted, onClose, onError }) => {
  const [value, setValue] = useState("");
  const [sending, setSending] = useState(false);
  const requestId = useRef<string | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => box.current?.focus(), []);

  const submit = async () => {
    const textValue = value.trim();
    if (!textValue || sending) return;
    setSending(true);
    requestId.current = requestId.current || newRequestId();
    const response = await post(textValue, requestId.current);
    setSending(false);
    if (response.ok) {
      requestId.current = null;
      setValue("");
      onPosted();
      return;
    }
    // A definite no from LinkedIn frees the next try to be a new request.
    if (!UNSETTLED.has(response.error || "")) requestId.current = null;
    onError(response.error);
  };

  return (
    <div style={{ marginTop: 8 }}>
      <Input ref={box} rows={2} placeholder={placeholder} value={value} disabled={sending} onChange={(e) => setValue(e.target.value)} />
      <ComposerRow>
        <Pill type="button" onClick={onClose}>
          Cancel
        </Pill>
        <Pill type="button" primary disabled={!value.trim() || sending} onClick={submit}>
          {sending ? busyLabel : action}
        </Pill>
      </ComposerRow>
    </div>
  );
};

const PersonRow: FC<{ person: Person; extra?: string | null; children?: React.ReactNode }> = ({ person, extra, children }) => {
  const name = person.name || "LinkedIn member";
  return (
    <li>
      <Circle url={person.picture} name={person.name} size={32} />
      <Who>
        <BylineTop>
          {person.url ? (
            <a href={person.url} target="_blank" rel="noopener noreferrer">
              {name}
            </a>
          ) : (
            <Name>{name}</Name>
          )}
          {extra && <MetaSpan>{` · ${extra}`}</MetaSpan>}
        </BylineTop>
        {person.headline && <Headline oneLine>{person.headline}</Headline>}
        {person.text && (
          <Text small style={{ marginTop: 4 }}>
            {person.text}
          </Text>
        )}
        {children}
      </Who>
    </li>
  );
};

/** One comment under a post: Like (with the reaction pill) and Reply, as the rep. */
const CommentRow: FC<{ initial: Person; item: ActivityItem; send: Send; onReplied: () => void }> = ({ initial, item, send, onReplied }) => {
  const [person, setPerson] = useState(initial);
  const [replying, setReplying] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const say = (textValue: string) => {
    setFlash(textValue);
    window.setTimeout(() => setFlash(null), 5000);
  };
  if (!person.commentUrn) return <PersonRow person={person} extra={ago(person.createdAt)} />;
  return (
    <PersonRow person={person} extra={ago(person.createdAt)}>
      <CommentActions>
        <ReactionControl
          compact
          mine={person.myReaction || null}
          apply={(on, reactionType, previousType, requestId) =>
            send({ part: "react", threadUrn: person.commentUrn, activityUrn: item.activityUrn, on, reactionType, previousType, requestId })
          }
          onChange={(mine, delta) => setPerson((p) => ({ ...p, myReaction: mine, reactions: Math.max(0, (p.reactions || 0) + delta) }))}
          onError={(error) => say(errorText(error))}
        />
        <span style={{ color: "#A3A2BD", fontSize: 12 }}>·</span>
        <LinkAction type="button" onClick={() => setReplying((r) => !r)}>
          {person.replies ? `Reply · ${person.replies}` : "Reply"}
        </LinkAction>
        {!!person.reactions && <Meta style={{ marginLeft: 6, fontSize: 12 }}>{count(person.reactions, "reaction", "reactions")}</Meta>}
      </CommentActions>
      {replying && (
        <Composer
          placeholder={`Reply to ${person.name || "this comment"}…`}
          action="Reply"
          busyLabel="Replying…"
          post={(textValue, requestId) =>
            send({ part: "comment", threadUrn: item.threadUrn, activityUrn: item.activityUrn, parentCommentUrn: person.commentUrn, text: textValue, requestId })
          }
          onPosted={() => {
            setPerson((p) => ({ ...p, replies: (p.replies || 0) + 1 }));
            setReplying(false);
            onReplied();
            say("Reply posted on LinkedIn.");
          }}
          onClose={() => setReplying(false)}
          onError={(error) => say(errorText(error))}
        />
      )}
      {flash && <Flash>{flash}</Flash>}
    </PersonRow>
  );
};

const Byline: FC<{ author?: Actor | null; age?: string | null; size?: number }> = ({ author, age, size = 40 }) => (
  <BylineRow>
    <Circle url={author?.picture} name={author?.name} size={size} />
    <Who>
      <BylineTop>
        <Name>{author?.name || "LinkedIn member"}</Name>
        {age && <MetaSpan>{` · ${age}`}</MetaSpan>}
      </BylineTop>
      {author?.headline && <Headline oneLine>{author.headline}</Headline>}
    </Who>
  </BylineRow>
);

type ListName = "reactors" | "comments";

/**
 * One post: who said it, the text, the counts (which open who reacted, or the
 * comments, under them), and Like and Comment as the rep.
 */
const PostView: FC<{ initial: ActivityItem; send: Send }> = ({ initial, send }) => {
  const [item, setItem] = useState<ActivityItem>(() => ({ ...initial, myReaction: initial.myReaction || (initial.liked ? "LIKE" : null) }));
  const [openList, setOpenList] = useState<ListName | null>(null);
  const [people, setPeople] = useState<{ list: ListName; people?: Person[]; total?: number; error?: string } | null>(null);
  const [commenting, setCommenting] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  // Each list is read once per post, and read again after the rep acts.
  const lists = useRef<Partial<Record<ListName, Promise<LinkedInCardResult<PeopleAnswer>>>>>({});

  const say = (textValue: string) => {
    setFlash(textValue);
    window.setTimeout(() => setFlash(null), 5000);
  };

  const toggle = async (list: ListName) => {
    if (openList === list) {
      setOpenList(null);
      return;
    }
    setOpenList(list);
    setPeople(null);
    lists.current[list] =
      lists.current[list] ||
      send<PeopleAnswer>(
        list === "reactors" ? { part: "reactors", threadUrn: item.threadUrn } : { part: "comments", socialDetailUrn: item.socialDetailUrn }
      );
    const response = await lists.current[list];
    if (!response?.ok) {
      delete lists.current[list];
      setPeople({ list, error: errorText(response?.error) });
      return;
    }
    setPeople({ list, people: response.people || [], total: response.total || 0 });
  };

  /** After the rep reacts or comments: the lists are read afresh next time. */
  const recount = () => {
    lists.current = {};
    setOpenList(null);
  };

  const reactions = count(item.reactions, "reaction", "reactions");
  const comments = count(item.comments, "comment", "comments");
  const reposts = count(item.reposts, "repost", "reposts");
  const parts: React.ReactNode[] = [];
  if (reactions)
    parts.push(
      item.threadUrn ? (
        <CountButton key="r" type="button" aria-expanded={openList === "reactors"} onClick={() => toggle("reactors")}>
          {reactions}
        </CountButton>
      ) : (
        <span key="r">{reactions}</span>
      )
    );
  if (comments)
    parts.push(
      item.socialDetailUrn ? (
        <CountButton key="c" type="button" aria-expanded={openList === "comments"} onClick={() => toggle("comments")}>
          {comments}
        </CountButton>
      ) : (
        <span key="c">{comments}</span>
      )
    );
  if (reposts) parts.push(<span key="s">{reposts}</span>);

  const shown = openList && people?.list === openList ? people : null;

  return (
    <Post>
      {item.header && <Context>{item.header}</Context>}
      {/* On the Comments tab, the prospect's own comment leads; the post is below it. */}
      {item.comment?.text && (
        <div style={{ marginBottom: 10 }}>
          <Byline author={{ name: item.comment.name, picture: item.comment.picture }} size={32} />
          <Text>{item.comment.text}</Text>
        </div>
      )}
      {item.comment?.text ? (
        <Embed>
          <Byline author={item.author} age={item.age} size={32} />
          {item.text && <Text small>{item.text}</Text>}
        </Embed>
      ) : (
        <div>
          <Byline author={item.author} age={item.age} />
          {item.text && <Text>{item.text}</Text>}
          {item.reshared && (item.reshared.text || item.reshared.author) && (
            <Embed>
              <Byline author={item.reshared.author} age={item.reshared.age} size={32} />
              {item.reshared.text && <Text small>{item.reshared.text}</Text>}
            </Embed>
          )}
        </div>
      )}
      {!!parts.length && (
        <Counts>
          {parts.map((part, i) => (
            <Fragment key={i}>
              {i > 0 && " · "}
              {part}
            </Fragment>
          ))}
        </Counts>
      )}
      {openList && (
        <People>
          {!shown ? (
            <Bones lines={2} />
          ) : shown.error ? (
            <Note>{shown.error}</Note>
          ) : !shown.people?.length ? (
            <Note>{openList === "reactors" ? "LinkedIn shows no reactions yet." : "LinkedIn shows no comments yet."}</Note>
          ) : (
            <>
              <PeopleList>
                {shown.people.map((person, i) =>
                  openList === "reactors" ? (
                    <PersonRow key={i} person={person} extra={person.reaction ? REACTIONS[person.reaction] || null : null} />
                  ) : (
                    <CommentRow key={i} initial={person} item={item} send={send} onReplied={() => delete lists.current.comments} />
                  )
                )}
              </PeopleList>
              {(shown.total || 0) > shown.people.length && item.url && (
                <OpenLink href={item.url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", marginTop: 8, fontSize: 13 }}>
                  {`${(shown.total || 0) - shown.people.length} more on LinkedIn`}
                </OpenLink>
              )}
            </>
          )}
        </People>
      )}
      {item.threadUrn && (
        <>
          <Actions>
            <ReactionControl
              mine={item.myReaction || null}
              apply={(on, reactionType, previousType, requestId) =>
                send({ part: "react", threadUrn: item.threadUrn, activityUrn: item.activityUrn, on, reactionType, previousType, requestId })
              }
              onChange={(mine, delta) => {
                setItem((i) => ({ ...i, myReaction: mine, liked: !!mine, reactions: Math.max(0, (i.reactions || 0) + delta) }));
                recount();
              }}
              onError={(error) => say(errorText(error))}
            />
            <ActionButton type="button" onClick={() => setCommenting((c) => !c)}>
              <CommentIcon />
              <span>Comment</span>
            </ActionButton>
            <ActionButton type="button" className="is-link" onClick={() => item.url && window.open(item.url, "_blank", "noopener")}>
              <OpenIcon />
              <span>Open on LinkedIn</span>
            </ActionButton>
          </Actions>
          {commenting && (
            <Composer
              placeholder="Add a comment…"
              action="Comment"
              busyLabel="Posting…"
              post={(textValue, requestId) => send({ part: "comment", threadUrn: item.threadUrn, activityUrn: item.activityUrn, text: textValue, requestId })}
              onPosted={() => {
                setItem((i) => ({ ...i, comments: (i.comments || 0) + 1 }));
                recount();
                setCommenting(false);
                say("Comment posted on LinkedIn.");
              }}
              onClose={() => setCommenting(false)}
              onError={(error) => say(errorText(error))}
            />
          )}
        </>
      )}
      {flash && <Flash>{flash}</Flash>}
    </Post>
  );
};

/* ── The tile ── */

interface Props {
  /** Which card this tile is: the person's profile, their recent activity, or both (the HubSpot modal). */
  variant: "profile" | "activity" | "full";
  /**
   * Given, the person is a HubSpot contact rather than the Outreach prospect in
   * the tile's context. The extension reads the LinkedIn URL the HubSpot card
   * passed, since it cannot look a HubSpot contact up by id.
   */
  hubspot?: { contactId: string; linkedInUrl?: string | null };
}

type Loaded<T> = { state: "loading" } | { state: "error"; error?: string } | { state: "ok"; value: T };

const LinkedInCards: FC<Props> = ({ variant, hubspot }) => {
  const crm = hubspot ? "HubSpot" : "Outreach";
  const showsActivity = variant === "activity" || variant === "full";
  const [prospectId, setProspectId] = useState<string | null>(null);
  const [noProspect, setNoProspect] = useState(false);
  const [profile, setProfile] = useState<Loaded<Profile>>({ state: "loading" });
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [kind, setKind] = useState(KINDS[0].kind);
  const [activity, setActivity] = useState<Record<string, Loaded<ActivityItem[]>>>({});

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    document.body.style.background = CANVAS;
    document.documentElement.style.background = CANVAS;
  }, []);

  useEffect(() => {
    if (hubspot) {
      setProspectId(hubspot.contactId);
      setNoProspect(!hubspot.contactId);
      return;
    }
    extensibilitySdk
      .init()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((ctx: any) => {
        const id = ctx?.prospect?.id ? String(ctx.prospect.id) : null;
        setProspectId(id);
        setNoProspect(!id);
      })
      .catch(() => {
        // Outside Outreach (local preview): ?prospectId=123.
        const id = isLocalHost ? new URLSearchParams(window.location.search).get("prospectId") : null;
        setProspectId(id);
        setNoProspect(!id);
      });
  }, [hubspot]);

  const send = useCallback<Send>(
    (message) =>
      linkedInCard({
        ...message,
        prospectId: prospectId || "",
        ...(hubspot ? { crm: "hubspot", contactId: hubspot.contactId, linkedinUrl: hubspot.linkedInUrl || "" } : {}),
      }),
    [prospectId, hubspot]
  );

  // The profile first: its read finds the member the activity query needs, so
  // the activity waits for it rather than making that read twice.
  const profileRead = useRef<Promise<unknown> | null>(null);
  useEffect(() => {
    if (!prospectId) return;
    setProfile({ state: "loading" });
    const read = send<{ profile: Profile; linkedinUrl?: string }>({ part: "profile" }).then((response) => {
      if (response.ok) {
        setProfile({ state: "ok", value: response.profile });
        setProfileUrl(response.profile?.url || response.linkedinUrl || null);
      } else {
        setProfile({ state: "error", error: response.error });
      }
    });
    profileRead.current = read;
  }, [prospectId, send]);

  useEffect(() => {
    if (!showsActivity || !prospectId || activity[kind]) return;
    setActivity((a) => ({ ...a, [kind]: { state: "loading" } }));
    // Only the open tab is read: each kind is one more LinkedIn request.
    (profileRead.current || Promise.resolve())
      .then(() => send<{ items: ActivityItem[] }>({ part: "activity", kind }))
      .then((response) =>
        setActivity((a) => ({
          ...a,
          [kind]: response.ok ? { state: "ok", value: response.items || [] } : { state: "error", error: response.error },
        }))
      );
  }, [showsActivity, prospectId, kind, activity, send]);

  const profileBody = () => {
    if (profile.state === "loading") return <Bones lines={6} />;
    if (profile.state === "error") return <Note>{errorText(profile.error, crm)}</Note>;
    return <ProfileBody profile={profile.value} />;
  };

  const activityBody = () => {
    const current = activity[kind];
    if (!current || current.state === "loading") return <Bones lines={5} />;
    if (current.state === "error") return <Note>{errorText(current.error, crm)}</Note>;
    if (!current.value.length) return <Note>{KINDS.find((k) => k.kind === kind)?.empty}</Note>;
    return (
      <Posts>
        {current.value.map((item, i) => (
          <PostView key={`${kind}-${item.activityUrn || i}`} initial={item} send={send} />
        ))}
      </Posts>
    );
  };

  const kindTabs = KINDS.map((entry) => (
    <Tab key={entry.kind} type="button" role="tab" aria-selected={kind === entry.kind} onClick={() => setKind(entry.kind)}>
      {entry.label}
    </Tab>
  ));
  const viewOnLinkedIn = profileUrl && (
    <OpenLink href={profileUrl} target="_blank" rel="noopener noreferrer">
      View on LinkedIn
    </OpenLink>
  );

  if (noProspect) {
    return (
      <Page>
        <Body>
          <Note>{hubspot ? "Open a contact to see their LinkedIn." : "Open a prospect to see their LinkedIn."}</Note>
        </Body>
      </Page>
    );
  }

  if (variant === "full") {
    // The HubSpot modal: the profile, then the activity, in one scroll.
    return (
      <Page>
        <Bar>{viewOnLinkedIn}</Bar>
        <Body>
          {profileBody()}
          {profile.state !== "error" && (
            <>
              <InlineBar>{kindTabs}</InlineBar>
              {activityBody()}
            </>
          )}
        </Body>
      </Page>
    );
  }

  return (
    <Page>
      <Bar>
        {variant === "activity" && kindTabs}
        {variant === "profile" && viewOnLinkedIn}
      </Bar>
      <Body>{variant === "profile" ? profileBody() : activityBody()}</Body>
    </Page>
  );
};

export default LinkedInCards;
